import { useEffect } from "react";
import { auth, messaging } from "./firebase";
import { saveDevicePushToken } from "./utils/pushToken";

const VAPID_KEY = "BDCKHSBv7FSlxFnOWseQBcrlmlxsDc0KJKRpUJnyDwCraKIslf8qS2wvDUyASjp2ZO6Mg-_gcB0ZnMOrK-d-Kg4";

export function usePushNotifications() {
  useEffect(() => {
    let unsubMessage = null;
    // En dev, React.StrictMode monta este efecto, lo desmonta y lo vuelve a
    // montar enseguida para detectar side effects no idempotentes. Si el
    // getToken() del primer montaje sigue esperando su transacción de
    // IndexedDB cuando el cleanup ya corrió, Firebase tira
    // "InvalidStateError: the database connection is closing" — es un
    // artefacto de StrictMode (no pasa así en producción), así que esta
    // bandera evita seguir escribiendo el token/armar el listener de un
    // montaje que ya fue descartado, y separado abajo evita loguearlo como
    // si fuera una falla real.
    let cancelled = false;

    const unsubAuth = auth.onAuthStateChanged(async (user) => {
      if (unsubMessage) {
        unsubMessage();
        unsubMessage = null;
      }

      if (!user) return;

      // App Android (WebView): no hay Notification ni service worker; el
      // token lo da Firebase nativo por el puente (MainActivity.getFcmToken
      // responde llamando a window.handleFcmToken). Va en otro campo porque
      // el servidor le arma el push distinto (ver sendNewNotificationV2).
      if (window.Android?.getFcmToken) {
        window.handleFcmToken = (token) => {
          if (!token || cancelled) return;
          saveDevicePushToken(user.uid, "fcmTokenAndroid", token);
        };
        window.Android.getFcmToken();
        return;
      }

      if (!("Notification" in window)) return;

      try {
        const permission = await Notification.requestPermission();
        if (permission !== "granted" || cancelled) return;

        // Wait for the registered SW to be ready, then pass it to getToken
        // so the FCM token is explicitly tied to our firebase-messaging-sw.js
        let swReg = null;
        if ("serviceWorker" in navigator) {
          swReg = await navigator.serviceWorker.ready;
        }
        if (cancelled) return;

        const token = await messaging.getToken({
          vapidKey: VAPID_KEY,
          ...(swReg ? { serviceWorkerRegistration: swReg } : {}),
        });
        if (cancelled) return;

        if (token) {
          await saveDevicePushToken(user.uid, "fcmToken", token);
        }
        if (cancelled) return;

        // Show notification when app is in the foreground
        unsubMessage = messaging.onMessage((payload) => {
          const d = payload?.data || {};
          const title = d.title || payload?.notification?.title || "Notiline";
          const body = d.body || payload?.notification?.body || "Nueva notificación";
          const url = d.url || "/notifications";

          const n = new Notification(title, {
            body,
            icon: "/logo192.png",
            badge: "/logo192.png",
            data: { url },
          });
          n.onclick = () => {
            window.focus();
            window.location.href = url;
            n.close();
          };
        });
      } catch (err) {
        if (cancelled && err?.name === "InvalidStateError") return;
        console.error("Push notifications error:", err);
      }
    });

    return () => {
      cancelled = true;
      unsubAuth();
      if (unsubMessage) unsubMessage();
    };
  }, []);
}
